import type { EntryPointRouteParams } from '@atlassian/jira-entry-points-plugin/src/common/types.tsx';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import PlaybookDetailQuery$parameters from '@atlassian/jira-relay/src/__generated__/PlaybookDetailQuery$parameters';
import { fg } from '@atlassian/jira-feature-gating';

/**
 * NOTE - Please update src/packages/playbooks/edit-screen/src/entrypoint.tsx
 * as that is used in integration test and ideally both should be same except queries
 */

const playbookEditViewEntryPoint = createEntryPoint({
	root: JSResourceForInteraction(() => {
		if (!__SERVER__) {
			return import(/* webpackChunkName: "async-spa-apps-playbooks-edit" */ './src');
		}
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return Promise.resolve({ default: () => null }) as unknown as Promise<{
			default: typeof import('./src').default;
		}>;
	}),
	getPreloadProps: ({ context, tenantContext }: EntryPointRouteParams) => {
		const { projectKey, playbookId = '' } = context.match.params;
		const { fromPlaybookId = '' } = context.query;
		if (!projectKey) {
			throw new Error('projectKey is required');
		}
		const { cloudId, activationId } = tenantContext;

		const isNewPlaybook = !!playbookId?.includes(NEW_PLAYBOOK_IDENTIFIER);

		const callablePlaybookId = getPlaybookId(playbookId, fromPlaybookId);

		const playbookAri = toPlaybookAri({
			siteId: cloudId,
			activationId,
			playbookId: callablePlaybookId,
		});

		const isNewPlaybookCheck = fg('playbook_duplicate_feature')
			? isNewPlaybook && !fromPlaybookId
			: isNewPlaybook;
		if (isNewPlaybookCheck) {
			// This is complete new playbook creation
			return {
				extraProps: {
					playbookAri,
					isNew: true,
					variant: 'new' as const,
				},
			};
		}
		return {
			queries: {
				PlaybookDetailQuery: {
					parameters: PlaybookDetailQuery$parameters,
					variables: {
						playbookAri,
					},
				},
			},
			extraProps: {
				playbookAri,
				isNew: false,
				variant: isNewPlaybook && !!fromPlaybookId ? ('copy' as const) : ('edit' as const),
			},
		};
	},
});

export default playbookEditViewEntryPoint;

function toPlaybookAri({
	siteId,
	activationId,
	playbookId,
}: {
	siteId: string;
	activationId: string;
	playbookId: string;
}) {
	return `ari:cloud:jira:${siteId}:playbook/activation/${activationId}/${playbookId}`;
}

const getPlaybookId = (playbookId: string | null, fromPlaybookId: string): string => {
	if (fg('playbook_duplicate_feature')) {
		if (fromPlaybookId) {
			return fromPlaybookId;
		}
		return playbookId || '';
	}
	return playbookId || '';
};

const NEW_PLAYBOOK_IDENTIFIER = '_new_';
